import { createContext, useContext } from "react";
import type { Currency, LineItem, VatTreatment } from "@/types";

export type RendererMode = "public" | "preview";

/** Quote-record fields blocks need but that don't live in block props
 *  (cover ref/contact/site; pricing currency/VAT). Supplied by the host. */
export interface QuoteContext {
  quote_number?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  company_name?: string;
  site_first_line_of_address?: string;
  site_address_line_1?: string;
  site_town?: string;
  site_county?: string;
  site_postcode?: string;
  /** Fills the `{{short_description}}` token in line-item text at render time. */
  project_description?: string;
  /** epoch ms (or ISO); rendered as the cover's "Valid until" line. */
  expiry_date?: string | number;
  currency?: Currency;
  vat_treatment?: VatTreatment;
}

/** Everything the block sub-components draw on, so the markup stays identical
 *  across public + preview and only behaviour (handlers/telemetry) forks on
 *  `mode`. Provided once by <QuoteRenderer>. */
export interface RendererContextValue {
  mode: RendererMode;
  quote?: QuoteContext;
  lineItems: LineItem[];
  selectedOptionalIds: number[];
  onToggleOptional: (id: number) => void;
  onAccept?: (name?: string) => void;
  onDecline?: (reason?: string) => void;
  acceptBusy?: boolean;
  acceptError?: string;
}

export const RendererCtx = createContext<RendererContextValue>({
  mode: "public",
  lineItems: [],
  selectedOptionalIds: [],
  onToggleOptional: () => {},
});

export function useRenderer(): RendererContextValue {
  return useContext(RendererCtx);
}
